import { ChevronRight } from '@mui/icons-material';
import { Box, Chip, ListItemButton, Paper, Typography, alpha } from '@mui/material';

import { ESpecie, People, UserPet } from 'shared/api';

import { getSubjectAge, getTypeVaccination } from '../../lib';
import { ExplainTarget } from '../../model/explain';

type Subject = { kind: 'people'; subject: People } | { kind: 'pet'; subject: UserPet };

export type SubjectCardProps = Subject & { onDiseaseClick: (target: ExplainTarget) => void };

// Кружки сделанных доз по болезни. У People бывает round/type_vaccination, у питомцев — нет (всегда index + 1).
const getRoundBadges = (props: Subject, diseaseId: string): Array<{ key: string; label: string | number }> => {
  if (props.kind === 'people') {
    return props.subject.listDoneVaccinations
      .filter((item) => item.diseaseId === diseaseId)
      .map((item, index) => ({
        key: item._id,
        label: item.round != null ? `${getTypeVaccination(item.type_vaccination)}${item.round}` : index + 1,
      }));
  }
  return props.subject.listDoneVaccinations
    .filter((item) => item.diseaseId === diseaseId)
    .map((item, index) => ({ key: item._id, label: index + 1 }));
};

export const SubjectCard = (props: SubjectCardProps) => {
  const { kind, subject, onDiseaseClick } = props;

  return (
    <Paper sx={{ p: 2, width: 320 }}>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <Typography fontWeight="bold">{subject.name}</Typography>
        {props.kind === 'pet' && <Chip size="small" label={props.subject.specie === ESpecie.Dog ? 'Dog' : 'Cat'} />}
      </Box>
      <Box display="flex" gap={1} mb={0.5}>
        <Typography color="text.secondary">Age</Typography>
        <Typography>{getSubjectAge(subject.birthday)}</Typography>
      </Box>
      <Box display="flex" gap={1} mb={1}>
        <Typography color="text.secondary">Country</Typography>
        <Typography>{subject.country?.name ?? '—'}</Typography>
      </Box>
      <Typography variant="overline" color="text.secondary" display="block">
        Selected diseases
      </Typography>
      <Box display="flex" flexDirection="column" gap={0.5}>
        {subject.listDisease.map((disease) => (
          <ListItemButton
            key={disease._id}
            onClick={() =>
              onDiseaseClick({
                kind,
                subjectId: subject._id,
                subjectName: subject.name,
                diseaseId: disease._id,
                diseaseName: disease.name,
              })
            }
            sx={{
              borderRadius: 1,
              px: 1,
              alignItems: 'center',
              '&:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08) },
            }}
          >
            <Box flex={1} minWidth={0}>
              <Typography color="primary.dark" fontWeight={500} sx={{ wordBreak: 'break-word' }}>
                {disease.name}
              </Typography>
              <Box mt={0.5} display="flex" alignItems="center" flexWrap="wrap" rowGap={1}>
                <Typography mr={1}>Rounds stamped:</Typography>
                {subject.listDoneVaccinations.length === 0
                  ? 'Vaccination has not started'
                  : getRoundBadges(props, disease._id).map((badge) => (
                      <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        flexShrink={0}
                        sx={{ borderRadius: '50%', border: '2px solid', borderColor: 'success.main' }}
                        mx={1}
                        key={badge.key}
                        width={26}
                        height={26}
                      >
                        {badge.label}
                      </Box>
                    ))}
              </Box>
            </Box>
            <ChevronRight color="primary" sx={{ ml: 1, flexShrink: 0 }} />
          </ListItemButton>
        ))}
      </Box>
    </Paper>
  );
};
